import { useToast } from "@/components/ui/use-toast.ts";
import { fetchApi } from "@/lib/api/index.ts";
import { useCurrentUser, UserRole } from "@/lib/api/user.ts";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
  InfiniteData,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

export interface Share {
  id: string;
  sharedWith: UserSummary;
  sharedBy: UserSummary;
  isRead: boolean;
  readAt: string | null;
  permission: "view";
}

export interface ShareableEntityNote {
  id: string;
  content: string;
  noteType: string | null;
  metadata: Record<string, unknown>;
}

export interface MomentImage {
  key: string;
  url: string;
  thumbnailKey: string;
  thumbnailUrl: string;
}

export interface ShareableEntityMoment {
  id: string;
  caption: string | null;
  images: MomentImage[];
}

export interface SwapImage {
  key: string;
  url: string;
  thumbnailKey: string;
  thumbnailUrl: string;
}

export type SwapStatus = "pending_mentor" | "pending_teen" | "completed";

export interface ShareableEntitySwap {
  id: string;
  promptId: string;
  customTitle?: string;
  status: SwapStatus;
  menteeImage: SwapImage | null;
  mentorImage: SwapImage | null;
}

export type EntityStatus = "draft" | "scheduled" | "published" | "archived";

export type ShareableEntityType = "note" | "moment" | "swap" | "activity";

export interface ShareableEntityActivity {
  id: string;
  activityType: string;
  status: string;
  completedAt: string | null;
}

export interface ShareableEntity {
  id: string;
  entityType: ShareableEntityType;
  status: EntityStatus;
  owner: UserSummary;
  title: string | null;
  description: string | null;
  metadata: Record<string, unknown>;
  note: ShareableEntityNote | null;
  moment: ShareableEntityMoment | null;
  swap: ShareableEntitySwap | null;
  activity: ShareableEntityActivity | null;
  shares: Share[];
  created_at: string;
  updated_at: string;
  scheduledAt: string | null;
  isNew?: boolean;
  isRead?: boolean;
}

export interface ShareableEntitiesResponse {
  entities: ShareableEntity[];
}

export type EntityFilter = "owned" | "shared" | "scheduled" | "all";

export interface GetUserEntitiesParams {
  menteeId?: string;
  page?: number;
  limit?: number;
  filter?: EntityFilter;
  readStatus?: "unread" | "all";
}

export interface UserSummary {
  id: string;
  name: string;
  first_name?: string | null;
  role: UserRole;
  profile_picture_url: string | null;
}

export interface EntityListResponseEntity {
  id: string;
  entity_type: string;
  status?: string;
  title: string | null;
  description: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  scheduled_at?: string | null;
  mentorship_id: string | null;
  owner: UserSummary;
  note: {
    id: string;
    content: string;
    note_type: string | null;
    metadata: Record<string, unknown>;
  } | null;
  moment: {
    id: string;
    caption: string | null;
    images: MomentImage[];
  } | null;
  swap: {
    id: string;
    custom_title?: string;
    mentee_image: SwapImage | null;
    mentor_image: SwapImage | null;
    mentorship_id: string;
    prompt_id: string;
    status: SwapStatus;
  } | null;
  activity: {
    id: string;
    activity_type: string;
    status: string;
    completed_at: string | null;
  } | null;
  shares: {
    id: string;
    shared_with: UserSummary;
    shared_by: UserSummary;
    is_read: boolean;
    shared_at: string;
  }[];
}

export interface EntityListResponse {
  entities: EntityListResponseEntity[];
  page: number;
  limit: number;
  is_last_page: boolean;
  total_count: number;
}

export interface ShareableEntityResponse {
  entity: ShareableEntity;
}

export interface ShareResponse {
  share: Share;
}

export interface UnreadCountResponse {
  count: number;
}

export interface UnreadCountsResponse {
  counts: Record<string, number>;
}

export interface SchedulePayload {
  sendNow?: boolean;
  scheduleAt?: string;
}

export interface ShareableEntityCreateSchema {
  content: string;
  noteType?: string | null;
  title?: string | null;
  description?: string | null;
  metadata?: Record<string, unknown>;
  shareWithId?: string;
  schedule?: SchedulePayload;
}

export interface ShareableEntityUpdateSchema {
  title?: string | null;
  description?: string | null;
  metadata?: Record<string, unknown>;
  noteData?: {
    content?: string;
    noteType?: string | null;
    metadata?: Record<string, unknown>;
  };
  schedule?: SchedulePayload;
}

export const transformEntity = (
  entity: EntityListResponseEntity,
  currentUserId: string,
  menteeId: string,
): ShareableEntity => {
  const shares: Share[] = entity.shares.map((share) => ({
    id: share.id,
    sharedWith: share.shared_with,
    sharedBy: share.shared_by,
    isRead: share.is_read,
    readAt: share.is_read ? share.shared_at : null,
    permission: "view" as const,
  }));

  const currentUserShare = shares.find(
    (share) => share.sharedWith.id === currentUserId,
  );

  const isRead = currentUserShare?.isRead ?? false;
  const isUnread = !isRead;

  const isNew = entity.owner.id !== menteeId ? false : isUnread;

  return {
    id: entity.id,
    entityType: entity.entity_type as ShareableEntityType,
    status: (entity.status as EntityStatus) ?? "published",
    owner: entity.owner,
    title: entity.title,
    description: entity.description,
    metadata: entity.metadata,
    note: entity.note
      ? {
          id: entity.note.id,
          content: entity.note.content,
          noteType: entity.note.note_type,
          metadata: entity.note.metadata,
        }
      : null,
    moment: entity.moment
      ? {
          id: entity.moment.id,
          caption: entity.moment.caption,
          images: entity.moment.images,
        }
      : null,
    swap: entity.swap
      ? {
          id: entity.swap.id,
          promptId: entity.swap.prompt_id,
          customTitle: entity.swap.custom_title,
          status: entity.swap.status,
          menteeImage: entity.swap.mentee_image,
          mentorImage: entity.swap.mentor_image,
        }
      : null,
    activity: entity.activity
      ? {
          id: entity.activity.id,
          activityType: entity.activity.activity_type,
          status: entity.activity.status,
          completedAt: entity.activity.completed_at,
        }
      : null,
    shares,
    created_at: entity.created_at,
    updated_at: entity.updated_at,
    scheduledAt: entity.scheduled_at ?? (entity.metadata?.scheduledAt as string) ?? null,
    isRead,
    isNew,
  };
};

const enrichEntity = (
  entity: ShareableEntity,
  currentUserId: string,
  menteeId: string,
): ShareableEntity => {
  const currentUserShare = entity.shares.find(
    (share) => share.sharedWith.id === currentUserId,
  );

  const isRead = currentUserShare?.isRead ?? false;
  const isUnread = !isRead;

  const isNew = entity.owner.id !== menteeId ? false : isUnread;

  return {
    ...entity,
    isRead,
    isNew,
  };
};

interface TransformedEntityListResponse {
  entities: ShareableEntity[];
  page: number;
  limit: number;
  is_last_page: boolean;
  total_count: number;
}

export const useShareableEntities = (
  menteeId?: string,
  filter?: EntityFilter,
  readStatus?: "unread" | "all",
) => {
  const { data: currentUser } = useCurrentUser();

  return useInfiniteQuery<
    TransformedEntityListResponse,
    Error,
    InfiniteData<TransformedEntityListResponse>,
    (string | undefined | EntityFilter | "unread" | "all")[],
    number
  >({
    initialPageParam: 1,
    queryKey: ["shareable-entities", menteeId, filter, readStatus],
    queryFn: async ({ pageParam = 1 }) => {
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      const response = await fetchApi<EntityListResponse>({
        url: "/v2/shareable-entities",
        method: "GET",
        params: {
          menteeId,
          page: pageParam,
          limit: 20,
          filter: filter || "all",
          readStatus: readStatus || "all",
        },
      });

      if (!response.data) {
        throw new Error("No entities found");
      }

      const currentUserId = currentUser.id;
      return {
        ...response.data,
        entities: response.data.entities.map((entity) =>
          transformEntity(entity, currentUserId, menteeId || ""),
        ),
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.is_last_page ? undefined : lastPage.page + 1;
    },
    enabled: !!menteeId && !!currentUser,
  });
};

interface ShareableEntityQueryOptions {
  retry?: boolean;
  retryOnMount?: boolean;
  staleTime?: number;
}

export const useShareableEntity = (
  id: string,
  menteeId?: string,
  queryOptions?: ShareableEntityQueryOptions,
) => {
  const { data: currentUser } = useCurrentUser();

  return useQuery<ShareableEntity>({
    queryKey: ["shareable-entities", id],
    queryFn: async () => {
      const response = await fetchApi<ShareableEntityResponse>({
        url: `/v2/shareable-entities/${id}`,
        method: "GET",
      });

      if (!response.data?.entity) {
        throw new Error("Entity not found");
      }

      const currentUserId = currentUser?.id || "";

      return enrichEntity(response.data.entity, currentUserId, menteeId || "");
    },
    enabled: !!id && !!currentUser,
    retry: queryOptions?.retry,
    retryOnMount: queryOptions?.retryOnMount,
    staleTime: queryOptions?.staleTime,
  });
};

export const useCreateShareableEntityNote = (menteeId?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<ShareableEntity, Error, ShareableEntityCreateSchema>({
    mutationKey: ["shareable-entities-create-note"],
    mutationFn: async (data) => {
      const response = await fetchApi<ShareableEntityResponse>({
        url: "/v2/shareable-entities/notes",
        method: "POST",
        params: {
          menteeId,
        },
        data,
      });

      if (!response.data?.entity) {
        throw new Error("Note not created");
      }

      return response.data.entity;
    },
    onSuccess: () => {
      toast({
        title: "Note created",
        description: "The note has been created successfully",
      });
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Something went wrong",
        description,
        variant: "destructive",
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
        refetchType: "all",
      });
    },
  });
};

export const useUpdateShareableEntity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    ShareableEntity,
    Error,
    { id: string; data: ShareableEntityUpdateSchema }
  >({
    mutationKey: ["shareable-entities-update"],
    mutationFn: async ({ id, data }) => {
      const response = await fetchApi<ShareableEntityResponse>({
        url: `/v2/shareable-entities/${id}`,
        method: "PATCH",
        data,
      });

      if (!response.data?.entity) {
        throw new Error("Entity not updated");
      }

      return response.data.entity;
    },
    onSuccess: () => {
      toast({
        title: "Note updated",
        description: "The note has been updated successfully",
      });
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Something went wrong",
        description,
        variant: "destructive",
      });
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
        refetchType: "all",
      });
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities", variables.id],
        refetchType: "all",
      });
    },
  });
};

export const useDeleteShareableEntity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["shareable-entities-delete"],
    mutationFn: async ({ id }) => {
      await fetchApi({
        url: `/v2/shareable-entities/${id}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      toast({
        title: "Note deleted",
        description: "The note has been deleted successfully",
      });
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Something went wrong",
        description,
        variant: "destructive",
      });
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
        refetchType: "all",
      });
      queryClient.removeQueries({
        queryKey: ["shareable-entities", variables.id],
      });
    },
  });
};

export const useSendScheduledEntityNow = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ShareableEntity,
    Error,
    { id: string }
  >({
    mutationKey: ["shareable-entities-send-now"],
    mutationFn: async ({ id }) => {
      const response = await fetchApi<ShareableEntityResponse>({
        url: `/v2/shareable-entities/${id}`,
        method: "PATCH",
        data: { schedule: { sendNow: true } },
      });

      if (!response.data?.entity) {
        throw new Error("Failed to send entity");
      }

      return response.data.entity;
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
        refetchType: "all",
      });
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities", variables.id],
        refetchType: "all",
      });
    },
  });
};

export const useRescheduleEntity = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ShareableEntity,
    Error,
    { id: string; scheduleAt: string }
  >({
    mutationKey: ["shareable-entities-reschedule"],
    mutationFn: async ({ id, scheduleAt }) => {
      const response = await fetchApi<ShareableEntityResponse>({
        url: `/v2/shareable-entities/${id}`,
        method: "PATCH",
        data: { schedule: { scheduleAt } },
      });

      if (!response.data?.entity) {
        throw new Error("Failed to reschedule entity");
      }

      return response.data.entity;
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
        refetchType: "all",
      });
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities", variables.id],
        refetchType: "all",
      });
    },
  });
};

export const useMarkEntityRead = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["shareable-entities-mark-read"],
    mutationFn: async ({ id }) => {
      await fetchApi({
        url: `/v2/shareable-entities/${id}/read`,
        method: "POST",
        data: {},
      });
    },
    onSuccess: () => {
      toast({
        title: "Marked as read",
        description: "The note has been marked as read",
      });
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Something went wrong",
        description,
        variant: "destructive",
      });
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities", variables.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities-unread-count"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities-unread-counts"],
      });
    },
  });
};

export const useMarkEntityUnread = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["shareable-entities-mark-unread"],
    mutationFn: async ({ id }) => {
      await fetchApi({
        url: `/v2/shareable-entities/${id}/unread`,
        method: "POST",
        data: {},
      });
    },
    onSuccess: () => {
      toast({
        title: "Marked as unread",
        description: "The note has been marked as unread",
      });
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Something went wrong",
        description,
        variant: "destructive",
      });
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities", variables.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities-unread-count"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["shareable-entities-unread-counts"],
      });
    },
  });
};

export const useUnreadCount = (menteeId?: string, excludeTypes?: string[]) => {
  return useQuery<number>({
    queryKey: ["shareable-entities-unread-count", menteeId, excludeTypes],
    queryFn: async () => {
      const response = await fetchApi<UnreadCountResponse>({
        url: "/v2/shareable-entities/unread-count",
        method: "GET",
        params: {
          menteeId,
          excludeTypes: excludeTypes?.join(","),
        },
      });

      return response.data?.count || 0;
    },
    enabled: !!menteeId,
  });
};

export const useUnreadCounts = () => {
  return useQuery<Record<string, number>>({
    queryKey: ["shareable-entities-unread-counts"],
    queryFn: async () => {
      const response = await fetchApi<UnreadCountsResponse>({
        url: "/v2/shareable-entities/unread-counts",
        method: "GET",
      });

      return response.data?.counts || {};
    },
  });
};

// ============================================================================
// MOMENTS API
// ============================================================================

export interface PresignedUrlResponse {
  highQuality: {
    key: string;
    url: string;
  };
  thumbnail: {
    key: string;
    url: string;
  };
  expiresIn: number;
}

export interface DraftMomentResponse {
  entity: ShareableEntity | null;
}

export interface AddImageParams {
  key: string;
  url: string;
  thumbnailKey: string;
  thumbnailUrl: string;
  draftId?: string;
}

export interface PublishMomentParams {
  caption?: string;
  shareWithId?: string;
  schedule?: SchedulePayload;
}

export const usePresignedUrl = () => {
  return useMutation<
    PresignedUrlResponse,
    Error,
    { filename: string; contentType?: string }
  >({
    mutationKey: ["moments-presigned-url"],
    mutationFn: async ({ filename, contentType }) => {
      const response = await fetchApi<PresignedUrlResponse>({
        url: "/v2/shareable-entities/moments/presigned-url",
        method: "GET",
        params: { filename, contentType },
      });

      if (!response.data) {
        throw new Error("Failed to get presigned URL");
      }

      return response.data;
    },
  });
};

interface AddImageResponse {
  momentId: string;
  images: MomentImage[];
}

export const useAddImageToMoment = (menteeId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<AddImageResponse, Error, AddImageParams>({
    mutationKey: ["moments-add-image"],
    mutationFn: async (params) => {
      const response = await fetchApi<AddImageResponse>({
        url: "/v2/shareable-entities/moments/images",
        method: "POST",
        params: { menteeId },
        data: params,
      });

      if (!response.data?.momentId) {
        throw new Error("Failed to add image");
      }

      return response.data;
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["moments-draft"],
      });
    },
  });
};

export const useDraftMoment = (menteeId?: string) => {
  return useQuery<ShareableEntity | null>({
    queryKey: ["moments-draft", menteeId],
    queryFn: async () => {
      const response = await fetchApi<DraftMomentResponse>({
        url: "/v2/shareable-entities/moments/draft",
        method: "GET",
        params: { menteeId },
      });

      return response.data?.entity || null;
    },
    enabled: !!menteeId,
  });
};

export const useRemoveImageFromMoment = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { momentId: string; imageKey: string }>({
    mutationKey: ["moments-remove-image"],
    mutationFn: async ({ momentId, imageKey }) => {
      await fetchApi({
        url: `/v2/shareable-entities/moments/${momentId}/images/${encodeURIComponent(imageKey)}`,
        method: "DELETE",
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["moments-draft"],
      });
    },
  });
};

interface PublishMomentResponse {
  moment: ShareableEntity;
}

export const usePublishMoment = (menteeId?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    ShareableEntity,
    Error,
    { momentId: string; params: PublishMomentParams }
  >({
    mutationKey: ["moments-publish"],
    mutationFn: async ({ momentId, params }) => {
      const response = await fetchApi<PublishMomentResponse>({
        url: `/v2/shareable-entities/moments/${momentId}/publish`,
        method: "PATCH",
        params: { menteeId },
        data: params,
      });

      if (!response.data?.moment) {
        throw new Error("Failed to publish moment");
      }

      return response.data.moment;
    },
    onSuccess: () => {
      toast({
        title: "Moment sent",
        description: "Your moment has been shared successfully",
      });
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Failed to send moment",
        description,
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["moments-draft"],
      });
    },
  });
};

// ============================================================================
// INTERACTIONS API
// ============================================================================

export type InteractionType = "comment" | "reaction";

export interface EntityInteraction {
  id: string;
  interactionType: InteractionType;
  content: string;
  user: UserSummary;
  created_at: string;
  metadata?: Record<string, unknown>;
}

export interface InteractionsResponse {
  interactions: EntityInteraction[];
}

export interface AddInteractionParams {
  type: InteractionType;
  content: string;
  metadata?: Record<string, unknown>;
}

export const useEntityInteractions = (entityId?: string) => {
  return useQuery<EntityInteraction[]>({
    queryKey: ["entity-interactions", entityId],
    queryFn: async () => {
      const response = await fetchApi<InteractionsResponse>({
        url: `/v2/shareable-entities/${entityId}/interactions`,
        method: "GET",
      });

      return response.data?.interactions || [];
    },
    enabled: !!entityId,
  });
};

export const useAddInteraction = (menteeId?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    EntityInteraction,
    Error,
    { entityId: string; params: AddInteractionParams }
  >({
    mutationKey: ["entity-add-interaction", menteeId],
    mutationFn: async ({ entityId, params }) => {
      const response = await fetchApi<{ interaction: EntityInteraction }>({
        url: `/v2/shareable-entities/${entityId}/interactions`,
        method: "POST",
        params: { menteeId },
        data: params,
      });

      if (!response.data?.interaction) {
        throw new Error("Failed to add interaction");
      }

      return response.data.interaction;
    },
    onSuccess: (_, variables) => {
      const message =
        variables.params.type === "comment" ? "Comment sent" : "Reaction sent";
      toast({
        title: message,
        description: "Your response has been shared",
      });
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Failed to send",
        description,
        variant: "destructive",
      });
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["entity-interactions", variables.entityId],
      });
    },
  });
};

// ============================================================================
// SWAPS API
// ============================================================================

export const useSwapPresignedUrl = () => {
  return useMutation<
    PresignedUrlResponse,
    Error,
    { filename: string; contentType?: string }
  >({
    mutationKey: ["swaps-presigned-url"],
    mutationFn: async ({ filename, contentType }) => {
      const response = await fetchApi<PresignedUrlResponse>({
        url: "/v2/shareable-entities/swaps/presigned-url",
        method: "GET",
        params: { filename, contentType },
      });

      if (!response.data) {
        throw new Error("Failed to get presigned URL");
      }

      return response.data;
    },
  });
};

export interface CreateSwapParams {
  promptId: string;
  customTitle?: string;
  mentorshipId: string;
  image: SwapImage;
  schedule?: SchedulePayload;
}

interface CreateSwapResponse {
  swap: ShareableEntity;
}

export const useCreateSwap = () => {
  const queryClient = useQueryClient();

  return useMutation<ShareableEntity, Error, CreateSwapParams>({
    mutationKey: ["swaps-create"],
    mutationFn: async (params) => {
      const response = await fetchApi<CreateSwapResponse>({
        url: "/v2/shareable-entities/swaps",
        method: "POST",
        data: params,
      });

      if (!response.data?.swap) {
        throw new Error("Failed to create swap");
      }

      return response.data.swap;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
        refetchType: "all",
      });
    },
  });
};

export interface AddSwapImageParams {
  key: string;
  url: string;
  thumbnailKey: string;
  thumbnailUrl: string;
}

interface AddSwapImageResponse {
  swap: ShareableEntity;
}

export const useAddSwapImage = () => {
  const queryClient = useQueryClient();
  return useMutation<ShareableEntity, Error, { swapId: string; params: AddSwapImageParams }>({
    mutationKey: ["swaps-add-image"],
    mutationFn: async ({ swapId, params }) => {
      const response = await fetchApi<AddSwapImageResponse>({
        url: `/v2/shareable-entities/swaps/${swapId}/images`,
        method: "POST",
        data: params,
      });

      if (!response.data?.swap) {
        throw new Error("Failed to add image");
      }

      return response.data.swap;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["shareable-entities"],
        refetchType: "all",
      });
    },
  });
};
